import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

// ── Animation helpers ─────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true, amount: 0.1 },
  transition:  { duration: 0.52, ease: "easeOut", delay },
});

// ── Track Data ────────────────────────────────────────────────────
const TRACKS = [
  {
    id:     1,
    number: "01",
    title:  "Materials, Structures & Construction",
    short:  "Materials & Structures",
    accent: "#F58220",
    darkAccent: "#d96d12",
    lightBg: "#FFF7ED",
    border:  "#FED7AA",
    topics: [
      "Advances in Science and Technology of Cement, Admixtures and Concrete",
      "Concrete with Recycled Materials",
      "Sustainability of Concrete & Durability of Concrete",
      "Structural Health Monitoring",
    ],
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <rect x="6"  y="34" width="10" height="10" rx="2" fill="currentColor" opacity="0.9"/>
        <rect x="19" y="24" width="10" height="20" rx="2" fill="currentColor" opacity="0.65"/>
        <rect x="32" y="14" width="10" height="30" rx="2" fill="currentColor" opacity="0.40"/>
        <path d="M9 32 L22 22 L35 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9"  cy="32" r="2.5" fill="currentColor"/>
        <circle cx="22" cy="22" r="2.5" fill="currentColor"/>
        <circle cx="35" cy="12" r="2.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    id:     2,
    number: "02",
    title:  "Geotechnical, Geosynthetic & Geotextile Applications",
    short:  "Geotechnical Applications",
    accent: "#D97706",
    darkAccent: "#b45309",
    lightBg: "#FFFBEB",
    border:  "#FDE68A",
    topics: [
      "Soil Stabilization Material and Methods",
      "Reinforced Earth Structures and Materials",
      "Ground Investigation & Ground Risk Management",
      "Ground Improvement Techniques",
    ],
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <path d="M4 40 Q12 26 24 30 Q36 34 44 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M4 44 Q12 34 24 38 Q36 42 44 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.4"/>
        <circle cx="24" cy="20" r="5" fill="currentColor" opacity="0.55"/>
        <path d="M10 44 L10 36 M20 44 L20 32 M30 44 L30 36 M40 44 L40 28"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
      </svg>
    ),
  },
  {
    id:     3,
    number: "03",
    title:  "Water Resources & Environmental Pollution",
    short:  "Water Resources",
    accent: "#EA580C",
    darkAccent: "#c2410c",
    lightBg: "#FFF7ED",
    border:  "#FDBA74",
    topics: [
      "Water Resources Planning and Management",
      "Ground Water Modeling & Recharge",
      "Water and Wastewater Treatment",
      "Municipal Solid Waste Characterization and Management",
    ],
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <path d="M6 24 Q12 14 24 20 Q36 26 42 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M6 32 Q12 22 24 28 Q36 34 42 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.45"/>
        <circle cx="10"  cy="38" r="4" fill="currentColor" opacity="0.35"/>
        <circle cx="24"  cy="40" r="4" fill="currentColor" opacity="0.65"/>
        <circle cx="38"  cy="36" r="4" fill="currentColor" opacity="0.35"/>
      </svg>
    ),
  },
  {
    id:     4,
    number: "04",
    title:  "Transportation, Surveying & Construction Management",
    short:  "Transportation & Management",
    accent: "#F59E0B",
    darkAccent: "#d97706",
    lightBg: "#FEFCE8",
    border:  "#FDE68A",
    topics: [
      "Material Characterization",
      "Pavement Materials Evaluation",
      "Application of Remote Sensing and GIS",
      "Time and Cost Optimization in Project Management",
      "Multidisciplinary Perspectives in Sustainable Infrastructure",
    ],
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <path d="M4 34 L44 34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M8 34 L16 20 L24 26 L32 14 L40 22"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="6"  y="36" width="6" height="8" rx="1.5" fill="currentColor" opacity="0.45"/>
        <rect x="21" y="36" width="6" height="8" rx="1.5" fill="currentColor" opacity="0.7"/>
        <rect x="36" y="36" width="6" height="8" rx="1.5" fill="currentColor" opacity="0.45"/>
      </svg>
    ),
  },
];

// ── Track Card ────────────────────────────────────────────────────
function TrackCard({ track, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative bg-white rounded-2xl overflow-hidden border flex flex-col cursor-pointer transition-all duration-300"
      style={{
        borderColor:  hovered ? track.accent : "#E5E7EB",
        boxShadow:    hovered
          ? `0 16px 48px -12px ${track.accent}35`
          : "0 2px 12px 0 rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-5px)" : "translateY(0)",
      }}
    >
      {/* Top accent bar */}
      <div className="h-[3px] w-full" style={{ background: track.accent }} />

      {/* Card Header */}
      <div
        className="px-7 pt-7 pb-6 flex items-start gap-5"
        style={{ backgroundColor: hovered ? track.lightBg : "#fff", transition: "background 0.3s" }}
      >
        {/* Icon box */}
        <div
          className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 p-2.5"
          style={{
            backgroundColor: hovered ? track.accent : track.lightBg,
            color:            hovered ? "#fff"       : track.accent,
          }}
        >
          {track.icon}
        </div>

        {/* Title block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className="text-[10px] font-black tracking-[0.2em] uppercase"
              style={{ color: track.accent }}
            >
              Track {track.number}
            </span>
          </div>
          <h3 className="text-gray-900 text-[17px] font-black leading-snug tracking-tight">
            {track.title}
          </h3>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-7 h-px bg-gray-100" />

      {/* Topics */}
      <div className="px-7 py-5 flex-1 flex flex-col gap-3">
        <p
          className="text-[10px] font-bold tracking-[0.18em] uppercase mb-1"
          style={{ color: track.accent }}
        >
          Research Topics
        </p>
        {track.topics.map((topic, i) => (
          <div key={i} className="flex items-start gap-3">
            <span
              className="mt-[5px] w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: track.accent }}
            />
            <span className="text-gray-600 text-[13px] leading-relaxed">{topic}</span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        className="mx-7 mb-6 flex items-center justify-between border-t pt-4"
        style={{ borderColor: hovered ? track.border : "#F3F4F6" }}
      >
        <span
          className="text-[12px] font-bold flex items-center gap-1.5 transition-all duration-200"
          style={{ color: track.accent }}
        >
          {track.topics.length} topics
        </span>
        <span
          className="flex items-center gap-1 text-[12px] font-bold transition-all duration-200"
          style={{ color: track.accent }}
        >
          View Details
          <ChevronRight
            size={15}
            className="transition-transform duration-200"
            style={{ transform: hovered ? "translateX(3px)" : "translateX(0)" }}
          />
        </span>
      </div>

      {/* Corner watermark number */}
      <span
        className="absolute bottom-3 right-5 text-[80px] font-black leading-none select-none pointer-events-none opacity-[0.04]"
        style={{ color: track.accent }}
      >
        {track.number}
      </span>
    </motion.div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function ResearchAreas() {
  return (
    <section className="w-full bg-gray-50 border-y border-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">

        {/* ── Section Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">

          <div className="max-w-2xl">
            <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#F58220]" />
              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                Conference Themes
              </span>
            </motion.div>

            <motion.h2
              {...fadeUp(0.1)}
              className="text-gray-900 text-3xl lg:text-[42px] font-black leading-tight tracking-tight mb-4"
            >
              Research
              <span className="text-[#F58220]"> Areas & Tracks</span>
            </motion.h2>

            <motion.p {...fadeUp(0.16)} className="text-gray-500 text-[15px] leading-relaxed max-w-xl">
              SMPACE-2026 invites original research contributions across four core tracks
              covering the full spectrum of sustainable civil engineering innovation.
            </motion.p>
          </div>

          {/* Right — stat pills */}
          <motion.div {...fadeUp(0.2)} className="flex flex-wrap gap-3 flex-shrink-0">
            {[
              { value: "4",   label: "Research Tracks"    },
              { value: "17+", label: "Sub-Topics"         },
              { value: "10",  label: "Pages Max Per Paper" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl px-5 py-3 shadow-sm text-center"
              >
                <p className="text-[#F58220] text-[22px] font-black leading-none">{s.value}</p>
                <p className="text-gray-400 text-[11px] mt-0.5 font-medium">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── 2x2 Cards Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {TRACKS.map((track, i) => (
            <TrackCard key={track.id} track={track} index={i} />
          ))}
        </div>

        {/* ── Publication Note ── */}
        <motion.div
          {...fadeUp(0.25)}
          className="mt-10 bg-white border border-gray-100 rounded-2xl px-7 py-6 flex flex-col sm:flex-row sm:items-center gap-5 shadow-sm"
        >
          {/* Left icon */}
          <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-[#F58220]">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M8 7h8M8 11h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 text-[15px] font-black leading-tight mb-1">
              Publication in Springer Proceedings
            </p>
            <p className="text-gray-500 text-[13px] leading-relaxed">
              Select high-quality papers from all tracks will be published as&nbsp;
              <span className="text-gray-800 font-semibold">SCOPUS Indexed Conference Proceedings</span>
              &nbsp;by Springer after rigorous double-blind peer review. Only original full research
              papers of minimum 4 pages are accepted.
            </p>
          </div>

          {/* CTA */}
          <Link
            to="/paper-submission"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-5 py-2.5 rounded-lg transition-all duration-200 group whitespace-nowrap"
          >
            Submission Guidelines
            <ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </Link>
        </motion.div>

        {/* ── Bottom CTA ── */}
        <motion.div
          {...fadeUp(0.3)}
          className="mt-6 relative overflow-hidden bg-[#111827] rounded-2xl px-8 py-9"
        >
          {/* Bg orbs */}
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-[#F58220]/8 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-[#F58220]/5 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-6 bg-[#F58220]" />
                <span className="text-[#F58220] text-[10px] font-bold tracking-[0.22em] uppercase">
                  Call for Papers
                </span>
              </div>
              <h3 className="text-white text-[22px] font-black leading-snug">
                Submit Your Original Research to SMPACE-2026
              </h3>
              <p className="text-white/50 text-[13px] mt-2">
                Abstract submission deadline:&nbsp;
                <span className="text-orange-400 font-semibold">June 15, 2026</span>
                &nbsp;·&nbsp; Full paper:&nbsp;
                <span className="text-orange-400 font-semibold">July 15, 2026</span>
              </p>
            </div>

            <div className="flex flex-col sm:items-end gap-3 flex-shrink-0">
              <Link
                to="/paper-submission"
                className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-6 py-3 rounded-lg transition-all duration-200 group"
              >
                Submit Your Paper
                <ArrowRight
                  size={14}
                  className="group-hover:translate-x-1 transition-transform duration-200"
                />
              </Link>
              <p className="text-white/35 text-[11px]">
                📧 ruceconference@reva.edu.in
              </p>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}